// sendEmailOtp.js
'use strict';
import fetch from '../resource/customFetch.js';
import getMeData from '../selector/getMeData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import fetchEmailDidYouMean from '../resource/fetchEmailDidYouMean.js';
import { Entry } from '../resource/loginRegisterConstants.js';
import getLoginData from '../selector/getLoginData.js';
import getOperationData from '../selector/getOperationData.js';
import getEmailCooldownTimestamp from '../selector/getEmailCooldownTimestamp.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import transitLoginViewStatus from '../action/transitLoginViewStatus.js';
import setTurnstileToken from '../action/setTurnstileToken.js';
import loadUrlQueryUtm from '../action/loadUrlQueryUtm.js';
import updateRegisterData from '../action/updateRegisterData.js';
import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import { RESEND_EMAIL_VERIFIED_CODE_SEC } from '../RemoteConfigKeys.js';

/**
 * Send email otp
 * @kind action
 * @param {string} {email} - action param.
 * @param {bool} {isEmailAgreed} - is email agreed.
 * @param {bool} {isResend} - is action call by verify modal.
 * @param {string} {[from = 'email']} - where is action called
 * @param {string} {[entry = 'login']} - entry string attached to magic link, ex: login, register, forgotPassword
 * @param {EmailTemplate} {templateId} - using template id.
 * @return {Promise} Action promise.
 */
const sendEmailOtp =
  ({
    email,
    isEmailAgreed,
    isResend,
    from = 'email',
    entry = 'login',
    templateId,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const selectPath = ['login', 'emailOtp', email];

    const isEmailFormatCorrect = getLoginData(state, 'isEmailFormatCorrect');
    if (!isEmailFormatCorrect) {
      return dispatch({ type: '' });
    }

    const sentCount = getOperationData(state, selectPath, 'sentCount') || 0;
    const emailCooldownTimestamp = getEmailCooldownTimestamp(
      state,
      ['login', 'emailOtp'],
      email
    );
    if (sentCount && Date.now() < emailCooldownTimestamp) {
      if (!isResend) {
        if (from === 'forgotPasswordEmail') {
          return dispatch(
            transitLoginViewStatus({ from, to: 'forgotPasswordEmailVerify' })
          );
        } else {
          return dispatch(transitLoginViewStatus({ from, to: 'emailVerify' }));
        }
      } else {
        return dispatch({ type: '' });
      }
    }

    const referralCode = getOperationData(
      getState(),
      ['acquisition', 'manual'],
      'code'
    );
    if (Entry.REGISTER === entry && referralCode) {
      const { utm_medium, utm_term } = getMeData(getState(), 'utm') || {};
      const link = getOperationData(getState(), ['acquisition'], 'link');
      const shouldReplaceUtm = !(
        utm_medium === 'referral' && utm_term === `referral_${referralCode}`
      );
      if (shouldReplaceUtm) {
        await dispatch(
          loadUrlQueryUtm({
            searchParams: new URLSearchParams(
              link
                ? link
                : `?utm_medium=referral&utm_term=referral_${referralCode}`
            ),
          })
        );
      }
    }

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
      },
    };

    const redirectUrl = new URL(location.origin + location.pathname);
    redirectUrl.searchParams.set('action', 'login-email');
    redirectUrl.searchParams.set('entry', entry);
    const utm = getMeData(getState(), 'utm');
    if (utm) {
      Object.keys(utm)
        .filter(key => utm[key])
        .forEach(key => redirectUrl.searchParams.append(key, utm[key]));
    }
    const url = getResourceUrl({ endpoint: '/verify/email' });

    url.searchParams.set('email', email);
    url.searchParams.set('next', redirectUrl.href);
    if (templateId) {
      url.searchParams.set('template_id', templateId);
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      await dispatch(setTurnstileToken());
      const turnstileToken = getOperationData(
        getState(),
        ['turnstile'],
        'token'
      );
      if (turnstileToken)
        fetchOptions.headers['X-Turnstile-Token'] = turnstileToken;
      // eslint-disable-next-line no-empty
    } catch (e) {}

    try {
      const response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        await handleFetchError({ response });
      }

      try {
        const didYouMean = await fetchEmailDidYouMean({ email });
        if (didYouMean && email !== didYouMean) {
          dispatch({
            type: MERGE_OPERATION_DATA,
            payload: { selectPath, data: { didYouMean } },
          });
        }
        // eslint-disable-next-line no-empty
      } catch (_) {}

      if (Entry.REGISTER === entry) {
        dispatch(
          updateRegisterData({ dataKey: 'isEmailAgreed', value: isEmailAgreed })
        );
      }

      if (!isResend) {
        if (from === 'forgotPasswordEmail') {
          dispatch({
            type: MERGE_OPERATION_DATA,
            payload: {
              selectPath: ['login'],
              data: { entry },
            },
          });
          dispatch(
            transitLoginViewStatus({ from, to: 'forgotPasswordEmailVerify' })
          );
        } else {
          dispatch(transitLoginViewStatus({ from, to: 'emailVerify' }));
        }
      }
      const cooldownSeconds = getRemoteConfigData(
        getState(),
        RESEND_EMAIL_VERIFIED_CODE_SEC
      );
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath,
          data: {
            sentCount: sentCount + 1,
            cooldownUnit: cooldownSeconds,
          },
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default sendEmailOtp;
